<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal :size="isSingle ? 'lg' : 'l'" :visible="modal.show" :data="model" @hidden="onHidden"
      :title="$t('Generate Batch Checks')" @ok.prevent="handleSubmit(onPreview)" :ok-title="$t('Preview Check')"
      :is-loading="loading" modal-class="mobile-full-screen" :disable-submit-btn="isButtonDisabled"
      @show="onShow">
      <div class="row col-md-12" v-if="payableInfo">
        <div class="row">
          <div class="col-md-12">
            <table class="info-table table table-sm settlement-payments-table ">
              <tr>
                <th style="width: 40%" class="border-top-0">{{ $t('Settlement') }}</th>
                <th style="width: 60%" class="border-top-0">{{ $t('Amount') }}</th>
              </tr>
              <tr>
                <td>{{ payableInfo.settlement_no }}</td>
                <td>
                  <currency-input-widget
                    :currency="currency" :label="false" :model="model" attribute="amount"
                    rules="required|min_value:0"
                  />
                  <div v-if="model.amount > payableInfo.payable_amount" class="error-note">
                    {{
                      $t('Amount entered is more than the payable amount, please note some items within this settlement might be selected as on hold, are you sure you would like to proceed?')
                    }}
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="2" class="text-right border-top-0 pb-0">
                  <div>
                    <b>{{ $t('Total Amount') }}</b>: {{ payableInfo.total_amount | asCurrency(currency) }}
                  </div>
                  <div class="mt-2">
                    <b>{{ $t('Payable Amount') }}</b>: {{ payableInfo.payable_amount | asCurrency(currency) }}
                  </div>
                  <div>
                    <div class="mt-2">
                      <b>{{ $t('Items On Hold') }}</b>: {{ payableInfo.items_on_hold }}
                    </div>
                    <div class="mt-2">
                      <b>{{ $t('Total Amount for Items On Hold') }}</b>:
                      {{ payableInfo.total_amount_on_hold | asCurrency(currency) }}
                    </div>
                    <div class="mt-2">
                      <b>{{ $t('Total Amount Paid for by the Buyers (Inc. On Hold)') }}</b>:
                      {{ payableInfo.total_paid_by_buyers | asCurrency(currency) }}
                    </div>
                    <div class="mt-2">
                      <b>{{ $t('Total Amount Paid for by the Buyers (Excl. On Hold)') }}</b>:
                      {{ payableInfo.total_paid_by_buyers_excl_on_hold | asCurrency(currency) }}
                    </div>
                  </div>
                </td>
              </tr>
            </table>
          </div>
          <div class="col-md-6">
            <input-widget rules="required" type="datepicker" :model="model" attribute="date"/>
          </div>
          <div class="col-md-6">
            <input-widget :model="model" rules="required|min_value:0" attribute="starting_check_number"/>
          </div>
          <div class="col-md-6" v-if="beneficiaries" >
            <input-widget :model="model" attribute="beneficiary_relation_uuid"
                          value-field="uuid" type="select2"
                          :display-field="getDisplayFieldForBeneficiary"
                          :items="beneficiaries"/>
          </div>
          <div class="col-md-6">
            <input-widget :model="model" attribute="reference_number" type="text" rules="max:11"/>
          </div>
          <div class="col-md-6" v-if="paymentBankAccounts && paymentBankAccounts.length > 0">
            <input-widget :model="model" attribute="bank_account" value-field="value"
                          display-field="${item.key}" type="select2" :items="paymentBankAccounts"/>
          </div>
          <div class="col-md-4">
            <input-widget is-switch :model="model" attribute="reconciled" type="checkbox"/>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="description" type="textarea"/>
          </div>
        </div>
      </div>
      <div v-else>
        <input-widget rules="required" type="datepicker" :model="model" attribute="date"/>
        <input-widget :model="model" rules="required|min_value:0" attribute="starting_check_number"/>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import InputWidget from "@/core/components/input-widget/InputWidget.vue";
  import SettlementService from "@/modules/settlements/SettlementService";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import CheckModel from "@/modules/checks/models/Check.model";
  import i18n from "@/shared/i18n";
  import EventBus from "@/plugins/event-bus";
  import {Formatter} from "@/core/services/formatter.service";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "GenerateBatchCheckModal",
    components: {
      CurrencyInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        model: new CheckModel(),
        payableInfo: null,
        beneficiaries: null
      }
    },
    watch: {
      async 'modal.selectedElements'() {
        if (this.isSingle) {
          const uuid = this.modal.selectedElements[0]
          let response = await SettlementService.getSettlement(uuid, {expand: "payable_amount,items_on_hold,total_amount_on_hold,total_paid_by_buyers,total_paid_by_buyers_excl_on_hold,currency,financial_items.inventory.receipt.relations.related_customer,financial_items.lot.reservation.inventory.receipt.relations.related_customer"})
          if (response.success) {
            this.setPayableInfo(response.body)
            this.setBeneficiaries(response.body)
          }
        }
      }
    },
    computed: {
      ...globalMapState({
        paymentBankAccounts: state => state.paymentBankAccounts,
      }),
      ...mapState({
        modal: state => state.generateBatchCheckModal,
      }),
      isButtonDisabled() {
        return !this.model.date || !this.model.starting_check_number
      },
      isSingle() {
        return this.modal.selectedElements ? this.modal.selectedElements.length === 1 : false
      },
      currency() {
        if (this.payableInfo) {
          return this.payableInfo.currency;
        }
        return null;
      },
    },
    methods: {
      ...mapActions(['hideGenerateBatchCheckModal', 'getTableData']),
      onHidden() {
        this.model = new CheckModel()
        this.loading = false
        this.beneficiaries = null
        this.payableInfo = null
        this.hideGenerateBatchCheckModal()
      },
      onShow() {
        this.model.date = Formatter.getNowAsDate()
      },
      async onSubmit() {
        if (this.payableInfo && (this.payableInfo.payable_amount < this.model.amount)) {
          let success = await this.$confirm(this.$i18n.t("Are you sure you want to pay more than the payable amount of the paid item?"))
          if (!success) {
            return;
          }
        }
        this.$evBus.$emit(ON_PDF_MODAL_CLOSE)
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t('Check'),
          actions: []
        })
        this.loading = true
        const data = this.generateRequestData(false)
        const resp = await SettlementService.createAndRetrieveChecksPdf(data)
        this.loading = false
        if (resp.success) {
          this.$toast('success', this.$i18n.t('Settlement(s) were successfully paid'));
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: resp.body,
            filename: 'Check'
          })
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE)
          let errorMessage = resp.body.non_field_errors ? resp.body.non_field_errors[0] : resp.body.starting_check_number[0]
          this.$errorDialog(errorMessage);
        }
        this.hideGenerateBatchCheckModal()
        await this.getTableData()
      },
      async onPreview() {
        this.loading = true
        const data = this.generateRequestData(true)
        const resp = await SettlementService.createAndRetrieveChecksPdf(data)
        this.loading = false
        const actions = [
          {
            display_name: i18n.t('Confirm and Create Payments'),
            handler: this.onSubmit
          },
          {
            display_name: i18n.t('Cancel'),
            handler: () => EventBus.$emit(ON_PDF_MODAL_CLOSE)
          }]
        if (resp.success) {
          this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
            title: this.$i18n.t('Check Preview'),
            actions: actions
          });
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: resp.body,
            filename: 'Check Preview'
          })
        } else {
          let errorMessage = resp.body.non_field_errors ? resp.body.non_field_errors[0] : resp.body.starting_check_number[0]
          this.$errorDialog(errorMessage);
        }
      },
      generateRequestData(isPreview = false) {
        let data = {
          uuids: this.modal.selectedElements,
          starting_check_number: this.model.starting_check_number,
          preview: isPreview,
          date: this.model.date
        }
        if (this.isSingle) {
          data['amount'] = this.model.amount
          data['beneficiary_relation_uuid'] = this.model.beneficiary_relation_uuid
          data['description'] = this.model.description
          data['reference_number'] = this.model.reference_number
          data['reconciled'] = this.model.reconciled
          data['bank_account'] = this.model.bank_account
        }
        return data
      },
      setPayableInfo(data) {
        this.payableInfo = {
          payable_amount: data.payable_amount,
          total_amount: data.total_amount,
          items_on_hold: data.items_on_hold,
          total_amount_on_hold: data.total_amount_on_hold,
          total_paid_by_buyers: data.total_paid_by_buyers,
          total_paid_by_buyers_excl_on_hold: data.total_paid_by_buyers_excl_on_hold,
          currency: data.currency.symbol,
          settlement_no: data.settlement_no
        }
        this.model.amount = data.payable_amount
      },
      setBeneficiaries(data) {
        const financialItems = data.financial_items || [];

        function getBeneficiariesData(data, receipt_no) {
          return data.filter(item => item.type === "beneficiary").map(item => ({...item, receipt_no}));
        }

        let beneficiaries = [];
        let receiptBeneficiariesFlag = [];
        financialItems.forEach(item => {
          if (item.inventory) {
            const inventoryRelations = item.inventory.receipt.relations;
            if (inventoryRelations && !receiptBeneficiariesFlag.includes(item.receipt.receipt_no)) {
              receiptBeneficiariesFlag.push(item.receipt.receipt_no)
              beneficiaries.push(...getBeneficiariesData(inventoryRelations, item.receipt.receipt_no));
            }
          } else if (item.lot) {
            const lotRelations = item.lot.reservation.inventory.receipt.relations;
            if (lotRelations && !receiptBeneficiariesFlag.includes(item.receipt.receipt_no)) {
              beneficiaries.push(...getBeneficiariesData(lotRelations, item.receipt.receipt_no));
              receiptBeneficiariesFlag.push(item.receipt.receipt_no)
            }
          }
        });
        this.beneficiaries = beneficiaries.length > 0 ? beneficiaries : null;
      },
      getDisplayFieldForBeneficiary(item) {
        const displayField = item.beneficiary_rate !== null ?
          `${item.receipt_no} - ${item.related_customer.display_name} - ${item.beneficiary_rate}%` :
          `${item.receipt_no} - ${item.related_customer.display_name}`
        return displayField
      }
    }
  }
</script>


<style lang="scss" scoped>
  .settlement-payments-table {
    tbody {
      td {
        vertical-align: middle;
        padding-top: .7em;
        padding-bottom: .7em;
      }
    }
  }

  .error-note {
    font-size: 12px;
    color: red;
  }
</style>

